import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { specialtiesApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, FlaskConical, Pencil, Trash2, Loader2 } from 'lucide-react';
import { formatDate } from '../lib/utils';

const emptyForm = { name: '', code: '' };

export default function AdminSpecialties() {
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create / Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, {id, name, code} = edit
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  // Delete confirmation
  const [deletingSpec, setDeletingSpec] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await specialtiesApi.getAll();
      setSpecialties(res.data);
    } catch (error) {
      console.error('Failed to fetch specialties:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (spec) => {
    setEditing(spec);
    setFormData({ name: spec.name, code: spec.code });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.code.trim()) {
      toast.error('Name and code are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await specialtiesApi.update(editing.id, formData);
        toast.success('Specialty updated');
      } else {
        await specialtiesApi.create(formData);
        toast.success('Specialty created');
      }
      setShowModal(false);
      setEditing(null);
      setFormData(emptyForm);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editing ? 'update' : 'create'} specialty`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingSpec) return;
    setDeleteLoading(true);
    try {
      await specialtiesApi.delete(deletingSpec.id);
      toast.success(`Specialty "${deletingSpec.name}" deleted`);
      setDeletingSpec(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete specialty');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <DashboardLayout title="Specialties">
      <div className="space-y-6" data-testid="admin-specialties">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 font-['Manrope']">Specialties</h2>
            <p className="text-slate-500 mt-1">{specialties.length} specialties</p>
          </div>
          <Button onClick={openCreate} className="bg-blue-900 hover:bg-blue-950" data-testid="add-spec-btn">
            <Plus size={16} className="mr-2" />
            Add Specialty
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && specialties.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                      No specialties yet. Click <strong>Add Specialty</strong> to create the first one.
                    </TableCell>
                  </TableRow>
                )}
                {specialties.map(spec => (
                  <TableRow key={spec.id} data-testid={`spec-row-${spec.code}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FlaskConical size={16} className="text-slate-400" />
                        {spec.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="px-2 py-1 bg-slate-100 rounded text-sm">{spec.code}</code>
                    </TableCell>
                    <TableCell>{formatDate(spec.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(spec)}
                          data-testid={`edit-spec-${spec.code}`}
                          title="Edit specialty"
                          className="h-8 w-8 p-0"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingSpec(spec)}
                          data-testid={`delete-spec-${spec.code}`}
                          title="Delete specialty"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={showModal} onOpenChange={(o) => { if (!o) { setShowModal(false); setEditing(null); }}}>
          <DialogContent data-testid="spec-modal">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Specialty' : 'Add Specialty'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Update the specialty name or code.' : 'Create a new specialty'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Clinical Trials"
                  data-testid="spec-name-input"
                />
              </div>
              <div>
                <Label>Code</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., CT"
                  maxLength={5}
                  data-testid="spec-code-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowModal(false); setEditing(null); }}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-blue-900 hover:bg-blue-950"
                data-testid="spec-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deletingSpec} onOpenChange={(o) => { if (!o) setDeletingSpec(null); }}>
          <AlertDialogContent data-testid="spec-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete specialty?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove <strong>{deletingSpec?.name}</strong> ({deletingSpec?.code})
                from the system. If any users, studies, or requests reference it, the delete will be blocked
                with an explanation — re-assign them first.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleteLoading}
                className="bg-red-600 hover:bg-red-700"
                data-testid="spec-delete-confirm-btn"
              >
                {deleteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
